import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { PLANS_BY_LANG } from "@/lib/i18n";

/**
 * Contextual paywall shown at the end of Grounding when the user chose
 * "Work on another scene" or "Work on my career" but their existing plan
 * mechanism blocks further access. Uses the SAME Stripe checkout endpoint
 * as the general Pricing page — no new payment path.
 */

const COPY = {
  pl: {
    scene: {
      title: "Wykorzystałeś już wszystkie sceny dostępne w swoim planie.",
      subtitle: "Jeśli chcesz przygotować kolejną scenę z Leo, wybierz sposób dalszej pracy:",
      offers: [
        {
          key: "leo_single_scene",
          name: "SINGLE SCENE",
          price: "19,90 PLN jednorazowo",
          body: "1 pełna scena i wszystkie narzędzia Scene Prep dla tej sceny.",
          cta: "KUP SINGLE SCENE — 19,90 PLN",
        },
        {
          key: "leo_start",
          name: "LEO START",
          price: "49 PLN/mies.",
          body: "5 pełnych scen miesięcznie, 5 dodatkowych wariantów oraz narzędzia pracy nad karierą.",
          cta: "WYBIERAM LEO START — 49 PLN",
        },
      ],
      allPlans: "Zobacz wszystkie plany",
      close: "Zamknij",
    },
    career: {
      title: "Wykorzystałeś już dostęp do funkcji kariery w swoim planie.",
      subtitle: "Jeśli chcesz kontynuować pracę z Leo nad karierą, wybierz plan:",
      offers: [
        {
          key: "leo_start",
          name: "LEO START",
          price: "49 PLN/mies.",
          body: "1 Career Path miesięcznie, limitowany Leo Career Chat, CRM do 20 kontaktów, Manager castingów do 10 aktywnych castingów, zadania i Gentle Reminders.",
          cta: "WYBIERAM LEO START — 49 PLN",
        },
        {
          key: "leo_monthly",
          name: "LEO MONTHLY",
          price: "119 PLN/mies.",
          body: "5 Career Paths miesięcznie, Leo Career Chat, CRM bez limitu, castingi bez limitu, zadania i Gentle Reminders.",
          cta: "WYBIERAM LEO MONTHLY — 119 PLN",
        },
      ],
      allPlans: "Zobacz wszystkie plany",
      close: "Zamknij",
    },
    reader: {
      title: "Wykorzystałeś 5 darmowych minut AI Readera. Wybierz plan, aby kontynuować pracę z wirtualnym partnerem scenicznym.",
      subtitle: "Wybierz plan:",
      offers: [
        { key: "leo_start", name: "LEO START", price: "", body: "10 scen miesięcznie i 180 minut AI Readera w każdym okresie rozliczeniowym, plus narzędzia pracy nad karierą.", cta: "" },
        { key: "leo_monthly", name: "LEO MONTHLY", price: "", body: "20 scen miesięcznie i 180 minut AI Readera w każdym okresie rozliczeniowym, plus pełne narzędzia kariery.", cta: "" },
      ],
      allPlans: "Zobacz wszystkie plany",
      close: "Zamknij",
    },
    reader_ended: {
      title: "Darmowa sesja AI Readera zakończona.",
      subtitle: "Pozostały Ci jeszcze darmowe minuty — możesz z nich skorzystać w kolejnej sesji. Jeśli chcesz regularnie pracować z wirtualnym partnerem scenicznym, wybierz plan:",
      offers: [
        { key: "leo_start", name: "LEO START", price: "", body: "10 scen miesięcznie i 180 minut AI Readera w każdym okresie rozliczeniowym, plus narzędzia pracy nad karierą.", cta: "" },
        { key: "leo_monthly", name: "LEO MONTHLY", price: "", body: "20 scen miesięcznie i 180 minut AI Readera w każdym okresie rozliczeniowym, plus pełne narzędzia kariery.", cta: "" },
      ],
      allPlans: "Zobacz wszystkie plany",
      close: "Zamknij",
    },
    opening: "Otwieram Stripe…",
    fail: "Nie udało się utworzyć płatności.",
  },
  en: {
    scene: {
      title: "You've used all the scenes included in your current plan.",
      subtitle: "To prepare another scene with Leo, choose how you'd like to continue:",
      offers: [
        {
          key: "leo_single_scene",
          name: "SINGLE SCENE",
          price: "$4.90 one-time",
          body: "1 complete scene with all Scene Prep tools included.",
          cta: "BUY SINGLE SCENE — $4.90",
        },
        {
          key: "leo_start",
          name: "LEO START",
          price: "$14.99/month",
          body: "5 complete scenes per month, 5 additional scene variations, plus career tools.",
          cta: "CHOOSE LEO START — $14.99",
        },
      ],
      allPlans: "See all plans",
      close: "Close",
    },
    career: {
      title: "You've reached the career limits of your current plan.",
      subtitle: "To keep working on your career with Leo, choose a plan:",
      offers: [
        {
          key: "leo_start",
          name: "LEO START",
          price: "$14.99/month",
          body: "1 Career Path per month, limited Leo Career Chat, CRM for up to 20 contacts, Casting Manager for up to 10 active auditions, tasks, and Gentle Reminders.",
          cta: "CHOOSE LEO START — $14.99",
        },
        {
          key: "leo_monthly",
          name: "LEO MONTHLY",
          price: "$31.90/month",
          body: "5 Career Paths per month, Leo Career Chat, unlimited CRM contacts, unlimited auditions, tasks, and Gentle Reminders.",
          cta: "CHOOSE LEO MONTHLY — $31.90",
        },
      ],
      allPlans: "See all plans",
      close: "Close",
    },
    reader: {
      title: "You've used your 5 free AI Reader minutes. Choose a plan to keep working with your virtual scene partner.",
      subtitle: "Choose a plan:",
      offers: [
        { key: "leo_start", name: "LEO START", price: "", body: "10 scenes per month and 180 AI Reader minutes every billing period, plus career tools.", cta: "" },
        { key: "leo_monthly", name: "LEO MONTHLY", price: "", body: "20 scenes per month and 180 AI Reader minutes every billing period, plus full career tools.", cta: "" },
      ],
      allPlans: "See all plans",
      close: "Close",
    },
    reader_ended: {
      title: "Your free AI Reader session has ended.",
      subtitle: "You still have free minutes left — you can use them in your next session. To work with your virtual scene partner regularly, choose a plan:",
      offers: [
        { key: "leo_start", name: "LEO START", price: "", body: "10 scenes per month and 180 AI Reader minutes every billing period, plus career tools.", cta: "" },
        { key: "leo_monthly", name: "LEO MONTHLY", price: "", body: "20 scenes per month and 180 AI Reader minutes every billing period, plus full career tools.", cta: "" },
      ],
      allPlans: "See all plans",
      close: "Close",
    },
    opening: "Opening Stripe…",
    fail: "Could not create payment.",
  },
};

// Backend classification mirror: which plan ids are subscriptions (recurring)
// vs one-time. Kept in sync with PLAN_META in /app/backend/server.py.
const SUBSCRIPTION_PLAN_IDS = new Set(["leo_start", "leo_monthly", "leo_light", "leo_annual"]);

export default function WhatNextPaywall({ open, kind, lang, onClose, onSeeAllPlans, source }) {
  const [busy, setBusy] = useState(null);
  const selectedRef = useRef(null); // last "started" plan for abandon-detection
  const startedRef = useRef(false); // did we send checkout_started at least once?
  const openWasTrue = useRef(false);
  // Per-offer checkbox state: { [plan_id]: { terms: bool, immediate: bool } }
  // Both default false; both required before we call POST /payments/checkout.
  const [consents, setConsents] = useState({});
  const consentOf = (id) => consents[id] || { terms: false, immediate: false };
  const setConsent = (id, key, val) =>
    setConsents((prev) => ({ ...prev, [id]: { ...consentOf(id), [key]: val } }));

  useEffect(() => {
    if (open) {
      openWasTrue.current = true;
      startedRef.current = false;
      selectedRef.current = null;
      trackEvent("paywall_viewed", { kind, source: source || null });
    } else if (openWasTrue.current) {
      // Closed after being shown. If a checkout_started was fired but never
      // completed within this open cycle, count it as abandoned.
      if (startedRef.current) {
        trackEvent("checkout_abandoned", {
          kind, source: source || null, plan: selectedRef.current,
        });
      }
      openWasTrue.current = false;
    }
    // eslint-disable-next-line
  }, [open]);

  if (!open) return null;
  const L = COPY[lang === "en" ? "en" : "pl"];
  const section = L[kind];

  // Pull every offer's price from the single source of truth (PLANS_BY_LANG
  // in /app/frontend/src/lib/i18n.js) so /pricing and this paywall never
  // drift. Only the plans actually shown in this component are handled;
  // other paywalls are out of scope for this change.
  const _plans = PLANS_BY_LANG?.[lang === "en" ? "en" : "pl"] || [];
  const _priceOf = (id) => (_plans.find((p) => p.id === id) || {}).price || "";
  const _isEn = lang === "en";
  const _overrideByKey = {
    leo_single_scene: (price) => ({
      price: _isEn ? `${price} one-time` : `${price} jednorazowo`,
      cta:   _isEn ? `BUY SINGLE SCENE — ${price}` : `KUP SINGLE SCENE — ${price}`,
    }),
    leo_start: (price) => {
      const regular = (_plans.find((p) => p.id === "leo_start") || {}).regular_price || "";
      // PL prices are intentionally hard-coded here (per paywall-only override):
      // first month 69 PLN, then 149 PLN/mies. EN still reads from PLANS_BY_LANG.
      const plFirst = "69 PLN";
      const plRegular = "149 PLN";
      return {
        price: (
          <>
            {_isEn ? "First month: " : "Pierwszy miesiąc: "}
            {_isEn ? price : plFirst}
            {(_isEn ? regular : plRegular) ? <br /> : null}
            {_isEn
              ? (regular ? `Then: ${regular}/month` : null)
              : `Potem: ${plRegular}/mies.`}
          </>
        ),
        cta:   _isEn ? `CHOOSE LEO START — ${price}` : `WYBIERAM LEO START — ${plFirst}`,
      };
    },
    leo_monthly: (price) => {
      // PL prices are intentionally hard-coded here (per paywall-only override):
      // first month 149 PLN, then 199 PLN/mies. EN still reads from PLANS_BY_LANG.
      const plFirst = "149 PLN";
      const plRegular = "199 PLN";
      return {
        price: _isEn ? `${price}/month` : (
          <>
            Pierwszy miesiąc: {plFirst}
            <br />
            Potem: {plRegular}/mies.
          </>
        ),
        cta:   _isEn ? `CHOOSE LEO MONTHLY — ${price}` : `WYBIERAM LEO MONTHLY — ${plFirst}`,
      };
    },
    leo_light: (price) => ({
      price: _isEn ? `${price}/month` : `${price} / mies.`,
      cta:   _isEn ? `CHOOSE LEO LIGHT — ${price}` : `WYBIERAM LEO LIGHT — ${price}`,
    }),
  };
  const resolvedOffers = section.offers.map((o) => {
    const price = _priceOf(o.key);
    const fn = _overrideByKey[o.key];
    return price && fn ? { ...o, ...fn(price) } : o;
  });

  const buy = async (lookup_key) => {
    // HARD-BLOCK on missing checkbox acceptance. Backend re-validates.
    const c = consentOf(lookup_key);
    if (!c.terms || !c.immediate) return;
    const suffix = lang === "pl" ? "_pln" : "_usd";
    const effective_key = `${lookup_key}${suffix}`;
    // Currency + price are taken from the visible offer copy so the event
    // matches exactly what the user just saw (no extra API calls here).
    const offer = section.offers.find((o) => o.key === lookup_key) || {};
    const currency = lang === "pl" ? "PLN" : "USD";
    trackEvent("paywall_plan_selected", {
      kind, source: source || null, plan: lookup_key, price: offer.price, currency,
    });
    selectedRef.current = lookup_key;
    setBusy(lookup_key);
    try {
      trackEvent("checkout_started", {
        kind, source: source || null, plan: lookup_key,
        price: offer.price, currency, lookup_key: effective_key,
      });
      startedRef.current = true;
      const r = await api.post("/payments/checkout", {
        lookup_key: effective_key,
        origin_url: window.location.origin,
        terms_accepted: true,
        privacy_acknowledged: true,
        immediate_service_requested: true,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || L.fail);
      setBusy(null);
    }
  };

  return (
    <div
      data-testid={`whatnext-paywall-${kind}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      className="fixed inset-0 z-[9998] flex items-start sm:items-center justify-center bg-stone-950/85 backdrop-blur-sm p-4 overflow-y-auto"
    >
      <div className="w-full max-w-2xl paper p-6 sm:p-8 relative my-auto max-h-[calc(100vh-2rem)] overflow-y-auto">
        <button
          data-testid="whatnext-paywall-close-btn"
          onClick={onClose}
          className="absolute top-4 right-4 text-stone-500 hover:text-stone-200"
          aria-label={L[kind].close}
        >
          <X size={18} />
        </button>
        <h2 className="serif text-2xl sm:text-3xl text-stone-100 pr-8 leading-snug">
          {section.title}
        </h2>
        <p className="text-stone-400 mt-3 text-sm leading-relaxed">{section.subtitle}</p>

        <div className="mt-6 space-y-4">
          {resolvedOffers.map((o) => {
            const c = consentOf(o.key);
            const canBuy = c.terms && c.immediate;
            const isSub = SUBSCRIPTION_PLAN_IDS.has(o.key);
            const isEN = lang === "en";
            return (
            <div
              key={o.key}
              data-testid={`whatnext-offer-${o.key}`}
              className="border border-stone-800 rounded-2xl p-5 bg-stone-900/60"
            >
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div className="serif text-xl text-stone-100">{o.name}</div>
                <div className="mono text-xs uppercase tracking-widest text-amber-400">{o.price}</div>
              </div>
              <p className="text-stone-300 text-sm mt-2 leading-relaxed">{o.body}</p>
              {isSub && (
                <p className="text-xs text-stone-400 mt-3 leading-relaxed" data-testid={`whatnext-renew-notice-${o.key}`}>
                  {isEN
                    ? "Your subscription renews automatically. You can cancel future renewals at any time."
                    : "Subskrypcja odnawia się automatycznie. Możesz anulować kolejne odnowienie w dowolnym momencie."}
                </p>
              )}
              <label className="mt-4 flex items-start gap-2 text-xs text-stone-300 leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`whatnext-consent-terms-${o.key}`}
                  checked={c.terms}
                  onChange={(e) => setConsent(o.key, "terms", e.target.checked)}
                  className="mt-0.5 shrink-0"
                />
                <span>
                  {isEN ? (
                    <>I accept the{" "}
                      <a href="/pricing#terms" target="_blank" rel="noopener noreferrer" className="text-amber-500 underline" data-testid={`whatnext-consent-terms-link-${o.key}`}>LEO Terms of Service</a>
                      {" "}and confirm that I have read the{" "}
                      <a href="/pricing#privacy" target="_blank" rel="noopener noreferrer" className="text-amber-500 underline" data-testid={`whatnext-consent-privacy-link-${o.key}`}>Privacy Policy</a>.
                    </>
                  ) : (
                    <>Akceptuję{" "}
                      <a href="/pricing#terms" target="_blank" rel="noopener noreferrer" className="text-amber-500 underline" data-testid={`whatnext-consent-terms-link-${o.key}`}>Regulamin LEO</a>
                      {" "}i potwierdzam zapoznanie się z{" "}
                      <a href="/pricing#privacy" target="_blank" rel="noopener noreferrer" className="text-amber-500 underline" data-testid={`whatnext-consent-privacy-link-${o.key}`}>Polityką Prywatności</a>.
                    </>
                  )}
                </span>
              </label>
              <label className="mt-2 flex items-start gap-2 text-xs text-stone-300 leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`whatnext-consent-immediate-${o.key}`}
                  checked={c.immediate}
                  onChange={(e) => setConsent(o.key, "immediate", e.target.checked)}
                  className="mt-0.5 shrink-0"
                />
                <span>
                  {isEN
                    ? "I request that the LEO service begins immediately after purchase, before the expiry of the 14-day withdrawal period. I acknowledge that if I exercise my right of withdrawal, I may be required to pay for the service provided up to the time of withdrawal, in accordance with applicable law."
                    : "Żądam rozpoczęcia świadczenia usługi LEO od razu po zakupie, przed upływem 14-dniowego terminu na odstąpienie od umowy. Przyjmuję do wiadomości, że w przypadku odstąpienia mogę być zobowiązany/a do zapłaty za świadczenie spełnione do chwili odstąpienia, zgodnie z obowiązującymi przepisami."}
                </span>
              </label>
              <button
                data-testid={`whatnext-buy-${o.key}`}
                disabled={busy === o.key || !canBuy}
                onClick={() => buy(o.key)}
                className="pill-btn mt-4 w-full sm:w-auto disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy === o.key ? L.opening : (isEN ? "Buy and pay" : "Kupuję i płacę")}
              </button>
            </div>
            );
          })}
        </div>

        <div className="mt-6 text-center">
          <button
            data-testid="whatnext-see-all-plans-btn"
            onClick={onSeeAllPlans}
            className="mono text-[11px] uppercase tracking-widest text-stone-400 hover:text-amber-400"
          >
            {section.allPlans}
          </button>
        </div>
      </div>
    </div>
  );
}
